// AGENT-A10: "View Raw Tables" modal.
//
// Centered overlay backed by /api/dashboard/raw-table. Lets a user inspect the
// joined working dataframes the dashboard's /report endpoint consumes
// (Invoice / Item Analysis / Client / Product Table). Opens when the TopBar
// dispatches the legacy `hsq:open-raw-tables` window event OR when any code
// flips the `uiStore.rawTablesOpen` boolean (Agent 3 may wire either path).
//
// Pagination + table-selector + page-size selector live in this component;
// the fetch hook (`useRawTable`) is intentionally local — we do NOT touch the
// shared api/hooks.ts module (owned by Agent 5).

import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useUiStore } from "../state/uiStore";
import "./RawTablesModal.css";

// AGENT-STATIC: Cloudflare static-bake mode — no live backend. The four raw
// tables are baked (capped sample) to /hsq-data/raw-tables.json; in this mode
// `useRawTable` fetches that file once and paginates client-side instead of
// hitting /api/dashboard/raw-table (which would fall through to the SPA's
// index.html and blow up res.json() with "Unexpected token '<'").
const USE_STATIC =
  typeof import.meta !== "undefined" &&
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_DASH_STATIC === "true";

/** Table-selector options surfaced in the dropdown. The `slug` matches the
 *  backend's case-insensitive alias map (see `_TABLE_ALIASES` in
 *  webapp/backend/app/routers/raw_tables.py). */
const TABLE_OPTIONS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: "invoice",        label: "Invoice" },
  { slug: "item_analysis",  label: "Item Analysis" },
  { slug: "client",         label: "Client" },
  { slug: "product",        label: "Product Table" },
] as const;

const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [25, 50, 100, 250] as const;

interface ColumnMeta {
  name: string;
  dtype: string;
}

interface RawTableResponse {
  table: string;
  page: number;
  size: number;
  total: number;
  columns: ColumnMeta[];
  rows: Array<Record<string, unknown>>;
  /** static-bake only: true table size (the baked `rows` are a capped sample). */
  fullTotal?: number;
}

// AGENT-STATIC: one entry per slug in the baked raw-tables.json. Fetched once
// (module-memoised) and sliced client-side for pagination.
interface StaticRawTable {
  table: string;
  columns: ColumnMeta[];
  rows: Array<Record<string, unknown>>;
  total: number;
  fullTotal?: number;
  error?: string;
}
let _staticRawTablesPromise: Promise<Record<string, StaticRawTable>> | null = null;
function loadStaticRawTables(): Promise<Record<string, StaticRawTable>> {
  if (!_staticRawTablesPromise) {
    _staticRawTablesPromise = fetch("/hsq-data/raw-tables.json", {
      headers: { Accept: "application/json" },
    }).then((r) => {
      if (!r.ok) throw new Error(`raw-tables.json ${r.status}`);
      return r.json() as Promise<Record<string, StaticRawTable>>;
    });
  }
  return _staticRawTablesPromise;
}

/** Local fetch hook — DO NOT export, do NOT add to api/hooks.ts. The
 *  modal owns this endpoint because no other component reads it. */
function useRawTable(
  table: string,
  page: number,
  size: number,
  filters: unknown,
  enabled: boolean,
): UseQueryResult<RawTableResponse> {
  return useQuery<RawTableResponse>({
    queryKey: ["raw-table", table, page, size, filters],
    queryFn: async ({ signal }) => {
      if (USE_STATIC) {
        const all = await loadStaticRawTables();
        const entry = all[table];
        if (!entry) throw new Error(`no baked data for table "${table}"`);
        if (entry.error) throw new Error(entry.error);
        const start = (page - 1) * size;
        return {
          table: entry.table,
          page,
          size,
          total: entry.total ?? entry.rows.length,
          columns: entry.columns,
          rows: entry.rows.slice(start, start + size),
          fullTotal: entry.fullTotal,
        } satisfies RawTableResponse;
      }
      const params = new URLSearchParams();
      params.set("table", table);
      params.set("page", String(page));
      params.set("size", String(size));
      if (filters && Array.isArray(filters) && filters.length > 0) {
        params.set("filters", JSON.stringify(filters));
      }
      const res = await fetch(`/api/dashboard/raw-table?${params.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal,
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(`raw-table fetch failed: ${res.status} ${detail}`);
      }
      return (await res.json()) as RawTableResponse;
    },
    enabled,
    // The user is actively browsing; keep cached pages fresh-ish but don't
    // hammer the backend on every modal re-open.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Format a single cell value for display. Pandas NaT/NaN already arrive
 *  as `null` thanks to the backend's `_scalar_clean`; render those as a
 *  muted "—". Objects/arrays get JSON-stringified for compact display. */
function formatCell(value: unknown): { text: string; isNull: boolean } {
  if (value === null || value === undefined) return { text: "—", isNull: true };
  if (typeof value === "boolean") return { text: value ? "true" : "false", isNull: false };
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return { text: "—", isNull: true };
    return { text: String(value), isNull: false };
  }
  if (typeof value === "string") {
    return { text: value, isNull: false };
  }
  // Object / array — JSON-stringify so the cell is at least readable.
  try {
    return { text: JSON.stringify(value), isNull: false };
  } catch {
    return { text: String(value), isNull: false };
  }
}

export function RawTablesModal(): JSX.Element | null {
  const isOpen = useUiStore((s) => s.rawTablesOpen);
  const setOpen = useUiStore((s) => s.setRawTablesOpen);
  const closeModal = useUiStore((s) => s.closeRawTables);

  // AGENT-A10: Agent 3's TopBar dispatches a `hsq:open-raw-tables` window
  // event (legacy path) — listen here so we open regardless of whether the
  // TopBar handler ultimately wires through the store boolean or the event.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("hsq:open-raw-tables", handler as EventListener);
    return () => {
      window.removeEventListener("hsq:open-raw-tables", handler as EventListener);
    };
  }, [setOpen]);

  const [table, setTable] = useState<string>(TABLE_OPTIONS[0].slug);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(50);
  const [pageInput, setPageInput] = useState<string>("1");

  // Reset page-1 whenever the table or page-size changes — otherwise a user
  // who was on page 47 of Invoice would see "page 47 of 2" on Client.
  useEffect(() => { setPage(1); }, [table, size]);
  useEffect(() => { setPageInput(String(page)); }, [page]);

  // Close on Esc.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [isOpen, closeModal]);

  const query = useRawTable(table, page, size, undefined, isOpen);

  // Compute the total page count even when the query is loading (use the
  // previously-cached `data` if available so the controls don't snap to 1).
  const total = query.data?.total ?? 0;
  const fullTotal = query.data?.fullTotal;  // static-bake: true size behind the sample
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / size)) : 1;

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const headerSummary = useMemo(() => {
    if (query.isLoading && !query.data) return "Loading…";
    if (query.isError) return "Failed to load";
    if (total === 0) return "0 rows";
    const start = (page - 1) * size + 1;
    const end = Math.min(page * size, total);
    const base = `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
    // static-bake serves a capped sample — say so rather than implying the
    // whole table is present.
    return fullTotal && fullTotal > total
      ? `${base} · sample of ${fullTotal.toLocaleString()}`
      : base;
  }, [query.isLoading, query.isError, query.data, total, page, size, fullTotal]);

  if (!isOpen) return null;

  const onBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === e.currentTarget) closeModal();
  };

  const onPageInputCommit = () => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.min(Math.max(1, parsed), totalPages);
    setPage(clamped);
  };

  const columns: ColumnMeta[] = query.data?.columns ?? [];
  const rows = query.data?.rows ?? [];

  return (
    <div className="rt-backdrop" role="dialog" aria-modal="true" onClick={onBackdropClick}>
      <div className="rt-modal" role="document">
        {/* ─── Header ─── */}
        <div className="rt-modal-head">
          <h2 className="rt-modal-title">View Raw Tables</h2>
          <span className="rt-meta">{headerSummary}</span>
          <button
            type="button"
            className="rt-modal-close"
            aria-label="Close"
            onClick={closeModal}
          >
            ×
          </button>
        </div>

        {/* ─── Controls row ─── */}
        <div className="rt-controls">
          <div className="rt-control">
            <label htmlFor="rt-table-select">Table</label>
            <select
              id="rt-table-select"
              className="rt-select"
              value={table}
              onChange={(e) => setTable(e.target.value)}
            >
              {TABLE_OPTIONS.map((opt) => (
                <option key={opt.slug} value={opt.slug}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="rt-control">
            <label htmlFor="rt-size-select">Page size</label>
            <select
              id="rt-size-select"
              className="rt-select"
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value, 10) || 50)}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="rt-spacer" />

          {columns.length > 0 && (
            <span className="rt-meta">{columns.length} columns</span>
          )}
        </div>

        {/* ─── Table viewport ─── */}
        <div className="rt-table-wrap">
          {query.isError ? (
            <div className="rt-state is-error">
              {(query.error as Error | undefined)?.message ?? "Failed to load table."}
            </div>
          ) : query.isLoading && !query.data ? (
            <div className="rt-state">Loading {table}…</div>
          ) : rows.length === 0 ? (
            <div className="rt-state">No rows on this page.</div>
          ) : (
            <table className="rt-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.name} title={`${c.name} (${c.dtype})`}>
                      {c.name}
                      <span className="rt-th-dtype">{c.dtype}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((c) => {
                      const cell = formatCell(row[c.name]);
                      return (
                        <td
                          key={c.name}
                          className={cell.isNull ? "rt-null" : undefined}
                          title={cell.text}
                        >
                          {cell.text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ─── Pagination footer ─── */}
        <div className="rt-pager">
          <button
            type="button"
            className="rt-pager-btn"
            disabled={!canPrev}
            onClick={() => setPage(1)}
            title="First page"
          >
            «
          </button>
          <button
            type="button"
            className="rt-pager-btn"
            disabled={!canPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            title="Previous page"
          >
            ‹
          </button>
          <span className="rt-pager-info">
            Page{" "}
            <input
              className="rt-input"
              type="text"
              inputMode="numeric"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onBlur={onPageInputCommit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onPageInputCommit();
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />{" "}
            of {totalPages.toLocaleString()}
          </span>
          <button
            type="button"
            className="rt-pager-btn"
            disabled={!canNext}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            title="Next page"
          >
            ›
          </button>
          <button
            type="button"
            className="rt-pager-btn"
            disabled={!canNext}
            onClick={() => setPage(totalPages)}
            title="Last page"
          >
            »
          </button>
          <div className="rt-spacer" />
          {query.isFetching && query.data && (
            <span className="rt-meta">Refreshing…</span>
          )}
        </div>
      </div>
    </div>
  );
}
